import type { ReactNode } from "react";

import { hubInvoiceNumberForDisplay } from "@integrator-src/commissions/qbo-payment-invoice-link";
import {
  BONUS_INVOICE_STATUS_OPTIONS,
  COMMISSION_INVOICE_STATUS_OPTIONS,
  type BonusInvoiceStatus,
  type CommissionInvoiceStatus,
} from "@/pages/commissions/commissionsApi";
import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { EM_DASH } from "@/lib/utils";

export type InvoiceDetailPeekContext = {
  invoice_mv_id: number;
  invoice_number: string;
  invoice_qb_doc_number?: string | null;
  invoice_date: string | null;
  sales_order_number?: string | null;
  sales_order_date?: string | null;
  sales_order_payment_terms_label?: string | null;
  due_date?: string | null;
  total_amount?: number | null;
  total_label?: string;
  qb_line_total_mismatch?: boolean;
  qb_compared_total?: number | null;
  qb_line_total_mismatch_reason?: string | null;
  end_user_name: string | null;
  pos_resale_end_user_name?: string | null;
  gosi_end_user_name?: string | null;
  rep_agency_name: string | null;
  fully_paid_date: string | null;
  fully_paid_label?: string;
  commission_status?: CommissionInvoiceStatus | null;
  bonus_status?: BonusInvoiceStatus | null;
  /** Run detail lines: show dollar commission instead of workflow status. */
  run_commission_amount?: number | null;
  /** Run detail bonus lines: show dollar bonus instead of workflow status. */
  run_bonus_amount?: number | null;
};

function peekField(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed != null && trimmed !== "" ? trimmed : EM_DASH;
}

function formatPeekDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") {
    return EM_DASH;
  }
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function statusLabel(
  status: CommissionInvoiceStatus | BonusInvoiceStatus | null | undefined,
  options: Array<{ value: string; label: string }>,
): string {
  if (status == null) {
    return EM_DASH;
  }
  return options.find((option) => option.value === status)?.label ?? status;
}

function formatPeekCurrency(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return EM_DASH;
  }
  return value.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatPeekInvoiceNumber(context: InvoiceDetailPeekContext): string {
  const displayNumber = hubInvoiceNumberForDisplay(context.invoice_number);
  const invoiceLabel = displayNumber !== "" ? displayNumber : EM_DASH;
  const qboDoc = context.invoice_qb_doc_number?.trim() ?? "";
  if (qboDoc === "" || hubInvoiceNumberForDisplay(qboDoc) === displayNumber) {
    return invoiceLabel;
  }
  return `${invoiceLabel} (QBO: ${qboDoc})`;
}

export function InvoiceDetailHoverHint({ context }: { context: InvoiceDetailPeekContext }): ReactNode {
  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Invoice", value: formatPeekInvoiceNumber(context) },
        {
          label: "MV document",
          value: <span className="tabular-nums">{context.invoice_mv_id}</span>,
        },
        { label: "Date", value: formatPeekDate(context.invoice_date) },
        { label: "Sales order", value: peekField(context.sales_order_number) },
        { label: "SO date", value: formatPeekDate(context.sales_order_date) },
        { label: "SO terms", value: peekField(context.sales_order_payment_terms_label) },
        { label: "Due date", value: formatPeekDate(context.due_date) },
        {
          label: context.total_label ?? "Total",
          value: (
            <span className="tabular-nums">
              {formatPeekCurrency(context.total_amount)}
              {context.qb_line_total_mismatch ? (
                <span className="ml-1.5 text-amber-600">
                  (QuickBooks {formatPeekCurrency(context.qb_compared_total)} vs Megaventory{" "}
                  {formatPeekCurrency(context.total_amount)})
                </span>
              ) : null}
            </span>
          ),
        },
        {
          label: context.pos_resale_end_user_name != null ? "POS end user" : "End user",
          value: peekField(context.end_user_name),
        },
        ...(context.gosi_end_user_name != null
          ? [{ label: "GOSI end user", value: peekField(context.gosi_end_user_name) }]
          : []),
        { label: "Rep agency", value: peekField(context.rep_agency_name) },
        {
          label: context.fully_paid_label ?? "Fully paid",
          value: formatPeekDate(context.fully_paid_date),
        },
        {
          label: "Commission",
          value:
            context.run_commission_amount != null
              ? formatPeekCurrency(context.run_commission_amount)
              : statusLabel(context.commission_status, COMMISSION_INVOICE_STATUS_OPTIONS),
        },
        {
          label: "Bonus",
          value:
            context.run_bonus_amount != null
              ? formatPeekCurrency(context.run_bonus_amount)
              : statusLabel(context.bonus_status, BONUS_INVOICE_STATUS_OPTIONS),
        },
      ]}
    />
  );
}
